import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { InfoIcon } from "lucide-react";
import {
  investorProfileQueryOptions,
  useClient,
  useSendHolderApprovalLink,
} from "creso-core/hooks";
import {
  findPrimaryHolder,
  findSecondHolder,
  formatHolderMobile,
} from "creso-core/utils";
import { AddSecondHolderFormData } from "creso-core/types";
import { Button, useMultiStepForm } from "web-design-system";
import { handleError } from "@/utils/error";
import {
  useRefetchClientStatus,
  useSecondHolderClientId,
} from "@crm/hooks/use-second-holder";
import { HolderCard } from "./holder-card";

export function NotifyHoldersStep({ onClose }: { onClose: () => void }) {
  const { formData, previousStep } =
    useMultiStepForm<AddSecondHolderFormData>();
  const clientId = useSecondHolderClientId();
  const { data: client } = useClient(clientId, clientId > 0);
  const { data: profiles } = useQuery(
    investorProfileQueryOptions(
      { client_id: clientId },
      { enabled: clientId > 0 }
    )
  );
  const primaryHolder = findPrimaryHolder(profiles);
  const secondHolder = findSecondHolder(profiles);
  const allApproved =
    !!primaryHolder?.is_approved && !!secondHolder?.is_approved;
  const refetchClientStatus = useRefetchClientStatus(clientId);

  const handleDone = async () => {
    onClose();
    await refetchClientStatus();
  };
  const { mutateAsync: sendHolderApprovalLink, isPending } =
    useSendHolderApprovalLink();

  const handleNotify = async () => {
    const accountId = client?.investment_account;
    if (!accountId) {
      handleError(
        new Error("Missing investment account"),
        "Failed to notify holders"
      );
      return;
    }

    const pendingProfileIds = [
      {
        holder: primaryHolder,
        profileId: primaryHolder?.investor_profile_guid,
      },
      {
        holder: secondHolder,
        profileId:
          formData.investor_profile_guid ?? secondHolder?.investor_profile_guid,
      },
    ]
      .filter(({ holder, profileId }) => !!profileId && !holder?.is_approved)
      .map(({ profileId }) => profileId as string);

    if (pendingProfileIds.length === 0) {
      toast.success("Holders notified successfully");
      onClose();
      return;
    }

    try {
      await Promise.all(
        pendingProfileIds.map((profileId) =>
          sendHolderApprovalLink({
            clientId,
            investment_account_guid: accountId,
            profile_id: profileId,
          })
        )
      );
      toast.success("Holders notified successfully");
      onClose();
    } catch (error) {
      handleError(error, "Failed to notify holders");
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 pt-3">
      <span className="text-lg font-semibold">Notify Holders</span>
      <div className="flex flex-col gap-4">
        <HolderCard
          name={client?.name ?? "—"}
          role="Primary holder"
          pan={client?.pan || "—"}
          mobile={formatHolderMobile(undefined, client?.phone_number)}
          email={client?.email || "—"}
          isApproved={primaryHolder?.is_approved}
        />
        <HolderCard
          name={formData.name ?? "—"}
          role="Second holder"
          pan={formData.pan || "—"}
          mobile={formatHolderMobile(formData.isd, formData.number)}
          email={formData.email || "—"}
          isApproved={secondHolder?.is_approved}
        />
      </div>
      <div className="mt-auto flex flex-col gap-4">
        <div className="flex items-start gap-2 rounded-lg border border-border bg-tag-slate p-3 text-tag-slate-foreground">
          <InfoIcon className="size-4 shrink-0 stroke-tag-slate-foreground" />
          <span className="text-xs">
            <span className="font-semibold">Approval Required.</span> Approval
            is required from both holders to finish joint account setup.
          </span>
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={previousStep}>
            Previous
          </Button>
          <Button
            type="button"
            disabled={isPending}
            onClick={allApproved ? handleDone : handleNotify}
          >
            {allApproved ? "Done" : "Notify"}
          </Button>
        </div>
      </div>
    </div>
  );
}
